<template>
    <!-- One fixed width for every action, so buttons match and the icons beside them line up in a column.
         Tasks with nothing to do (the conversation the user is in, a start on an expired cycle) say so in a
         label, and a manual completion lets the row's done icon speak, instead of showing a disabled button. -->
    <span class="ms-1 flex w-28 shrink-0 justify-end">
        <span v-if="statusLabel" class="whitespace-nowrap scaffold-details-small text-ink-muted" :data-testid="`picker-row-${action.kind}`">
            {{ statusLabel }}
        </span>
        <BaseButton
            v-else-if="action.kind !== 'marked-complete'"
            theme="neutral"
            size="sm"
            class="w-full justify-center"
            :disabled="isBusy"
            :aria-label="accessibleName"
            :data-testid="`picker-row-${action.kind}`"
            @click="emit('activate')">
            {{ action.text }}
        </BaseButton>
    </span>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import type { DecoratedPerformanceReview } from "~vue/types/performance_management";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { taskAction } from "./taskAction";

const props = withDefaults(defineProps<{ review: DecoratedPerformanceReview; currentConversationHashid?: string; isBusy?: boolean; startDisabled?: boolean }>(), {
    currentConversationHashid: "",
    isBusy: false,
    startDisabled: false,
});

const emit = defineEmits<{ activate: [] }>();
const { t } = useI18n();

const action = computed(() => taskAction(props.review, { currentConversationHashid: props.currentConversationHashid, startDisabled: props.startDisabled }, t));

// The visible word alone ("Continue") doesn't say which task or teammate it acts on, so the
// accessible name adds them, starting with the visible word so voice control still matches it.
const accessibleName = computed(() => {
    const task = props.review.cta?.label;
    if (!task) {
        return undefined;
    }
    const person = props.review.relationship?.reviewee_name;
    return person
        ? t("chat.performancePicker.actionForPerson", { action: action.value.text, task, person })
        : t("chat.performancePicker.actionForTask", { action: action.value.text, task });
});

const statusLabel = computed(() => {
    if (action.value.kind === "started") {
        return t("chat.performancePicker.status.currentlyViewing");
    }
    return action.value.kind === "unavailable" ? action.value.text : null;
});
</script>
